import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
} from '@mui/material';
import './manualOnboarding.css';
import { JSONContainer } from '../../../../../components/AccountOnboarding/ManualAccountOnboarding/JSONContainer';
import CustomButton from '../../../../../components/MasterComponent/Button/CustomButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useCopyText } from '../../../../../hooks/useCopyText';
import { COST_REPORTS_STEPS } from '../config';

interface CreateCostUsageReportProps {
  onComplete: () => void;
  onBack: () => void;
  s3BucketName?: string;
}

const CreateCostUsageReport: React.FC<CreateCostUsageReportProps> = ({
  onComplete,
  onBack,
  s3BucketName = '',
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [pathPrefix] = useState('cost-reports');
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const { isCopied, copyToClipboard } = useCopyText();

  // Steps data for Cost and Usage Report creation

  const handleCopyText = (index: number) => {
    let textToCopy = '';

    if (index === 2 && s3BucketName) {
      textToCopy = s3BucketName;
    }

    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy).then(
        () => {
          const newCompleted = { ...completed };
          newCompleted[index] = true;
          setCompleted(newCompleted);
        },
        (err) => {
          console.error('Could not copy text: ', err);
        }
      );
    } else {
      // If no text to copy, just mark the step as completed
      const newCompleted = { ...completed };
      newCompleted[index] = true;
      setCompleted(newCompleted);
    }
  };

  const handleNext = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const totalSteps = () => {
    return COST_REPORTS_STEPS(s3BucketName).length;
  };

  const allStepsCompleted = () => {
    return activeStep === totalSteps() - 1;
  };

  return (
    <Box className="createCostReportContainer">
      <Paper elevation={0} className="instructionsPaper">
        <p className="instructions">
          Follow these steps to create a Cost and Usage Report in your AWS
          console. This report will allow CloudForestX to analyze your cloud
          spending and provide cost optimization recommendations.
        </p>
      </Paper>

      <Box className="reportSettingsContainer">
        <Box className="reportPathDisplay">
          <Typography variant="subtitle1" fontWeight={500}>
            S3 Path Prefix (required):
          </Typography>
          <Paper elevation={0} className="bucketNamePaper">
            <div className="bucketNameText d-flex align-center">
              {pathPrefix}{' '}
              <IconButton
                size="small"
                onClick={() => copyToClipboard(pathPrefix)}
                className="copyNameButton"
              >
                {isCopied ? (
                  <CheckCircleOutlineIcon
                    style={{ color: '#3f51b5' }}
                    fontSize="small"
                  />
                ) : (
                  <ContentCopyIcon
                    style={{ color: '#3f51b5' }}
                    fontSize="small"
                  />
                )}
              </IconButton>
            </div>
          </Paper>
          <Typography variant="body2" color="textSecondary">
            This path prefix is required for CloudForestX to locate your reports
          </Typography>
        </Box>
      </Box>

      <Box className="costReportStepsContainer">
        <p className="costReportStepTitle">
          Cost and Usage Report Creation Steps
        </p>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          className="costReportStepper"
        >
          {COST_REPORTS_STEPS(s3BucketName).map((step: any, index: number) => (
            <Step key={index} completed={completed[index]}>
              <StepLabel>
                <h6>{step.name}</h6>
              </StepLabel>
              <StepContent>
                <JSONContainer
                  key={index}
                  index={index}
                  policy={step}
                  activeStep={activeStep}
                  totalSteps={totalSteps()}
                  onCopyJson={handleCopyText}
                  onNext={handleNext}
                  onBack={handleBack}
                  stepType="Step"
                  suggestedNameType="S3 path prefix"
                />
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      {allStepsCompleted() && (
        <Paper elevation={0} className="completionPaper">
          <Typography
            variant="body1"
            className="completionText font-13px fw-600"
          >
            You have successfully created your AWS Cost and Usage Report. It may
            take up to 24 hours for the first report to be delivered to your S3
            bucket. CloudForestX will automatically detect and process these
            reports once they're available.
          </Typography>
        </Paper>
      )}

      <Box className="navigationButtons">
        <CustomButton buttonType="outlined" onClickHandler={onBack}>
          Back
        </CustomButton>
        <div className="d-flex align-center">
          {!allStepsCompleted() && (
            <p className="warningMessageManualOnboarding mr-10">
              ( Please complete all AWS Cost and Usage Report configuration
              steps before proceeding. )
            </p>
          )}
          <CustomButton
            buttonType="contained"
            disabled={!allStepsCompleted()}
            onClickHandler={onComplete}
          >
            Next: Complete Onboarding
          </CustomButton>
        </div>
        {/* {allStepsCompleted() && (
          <CustomButton buttonType="contained" onClickHandler={onComplete}>
            Complete Onboarding
          </CustomButton>
        )} */}
      </Box>
    </Box>
  );
};

export default CreateCostUsageReport;
